/**
 * Reader count — scoped to pages containing [data-reader-count]
 * (i.e. News and views articles).
 *
 * Shows a Guardian-style "You've read N articles in the last year" panel at the
 * end of an article. Everything here is progressive enhancement and entirely
 * client-side:
 *
 * - Blitz caches the article HTML and serves the same bytes to every visitor,
 *   so the count cannot be personalised server-side. It is computed in the
 *   browser from localStorage and never leaves the device — nothing is sent to
 *   the server, so this adds no server-side processing of personal data.
 * - Counting is gated on the visitor having accepted the `analytics` cookie
 *   category (parsed from the CMP's own `cc_cookie`, the same shape the PHP side
 *   reads), plus an explicit in-widget on/off toggle.
 * - Withdrawing analytics consent, or turning the count off, deletes the stored
 *   reading history rather than merely pausing it.
 *
 * State model:
 *   eaukArticleReads    { v: 1, reads: [{ id, ts }, …] } — distinct articles in
 *                        the rolling 12 months. Re-reading one doesn't inflate.
 *   eaukArticleCountOptOut  '1' when the reader chose "turn off".
 */
(function () {
	const root = document.querySelector('[data-reader-count]');
	if (!root) return;

	const STORE_KEY = 'eaukArticleReads';
	const OPTOUT_KEY = 'eaukArticleCountOptOut';
	const STORE_VERSION = 1;
	const YEAR_MS = 365 * 24 * 60 * 60 * 1000;

	const articleId = root.getAttribute('data-article-id');
	// Below this, a lone "1 article" is more thin than welcome — keep counting
	// silently and only surface the panel once the reader is clearly engaged.
	const revealThreshold = parseInt(root.getAttribute('data-reveal-threshold'), 10) || 1;

	const panel = root.querySelector('[data-reader-count-panel]');
	const optedOutRow = root.querySelector('[data-reader-count-optedout]');
	const numberNodes = Array.prototype.slice.call(
		root.querySelectorAll('[data-reader-count-number]')
	);
	const nounNode = root.querySelector('[data-reader-count-noun]');
	const offBtn = root.querySelector('[data-reader-count-off]');
	const onBtn = root.querySelector('[data-reader-count-on]');

	// ── Storage (private mode / disabled storage must never throw) ───────────

	function readReads() {
		try {
			const raw = window.localStorage.getItem(STORE_KEY);
			if (!raw) return [];
			const data = JSON.parse(raw);
			return data && Array.isArray(data.reads) ? data.reads : [];
		} catch (e) {
			return [];
		}
	}

	function writeReads(reads) {
		try {
			window.localStorage.setItem(
				STORE_KEY,
				JSON.stringify({ v: STORE_VERSION, reads: reads })
			);
		} catch (e) {
			/* storage full or unavailable — the feature just no-ops */
		}
	}

	function clearReads() {
		try {
			window.localStorage.removeItem(STORE_KEY);
		} catch (e) {
			/* nothing to do */
		}
	}

	function isOptedOut() {
		try {
			return window.localStorage.getItem(OPTOUT_KEY) === '1';
		} catch (e) {
			return false;
		}
	}

	function setOptedOut(value) {
		try {
			if (value) {
				window.localStorage.setItem(OPTOUT_KEY, '1');
			} else {
				window.localStorage.removeItem(OPTOUT_KEY);
			}
		} catch (e) {
			/* nothing to do */
		}
	}

	// ── Consent ──────────────────────────────────────────────────────────────
	// Read the CMP cookie directly (same shape as AnalyticsService reads it
	// server-side) rather than the vanilla-cookieconsent API, so this module has
	// no load-order dependency on global.js.

	function hasAnalyticsConsent() {
		try {
			const raw = window.getCookie ? window.getCookie('cc_cookie') : '';
			if (!raw) return false;
			const data = JSON.parse(decodeURIComponent(raw));
			return !!(
				data &&
				Array.isArray(data.categories) &&
				data.categories.indexOf('analytics') !== -1
			);
		} catch (e) {
			return false;
		}
	}

	// ── Counting ─────────────────────────────────────────────────────────────

	function prune(reads) {
		const cutoff = Date.now() - YEAR_MS;
		return reads.filter(function (r) {
			return r && typeof r.ts === 'number' && r.ts > cutoff;
		});
	}

	function recordVisit() {
		const reads = prune(readReads());
		const seen = reads.some(function (r) {
			return String(r.id) === String(articleId);
		});
		if (articleId && !seen) {
			reads.push({ id: articleId, ts: Date.now() });
		}
		writeReads(reads);
		return reads;
	}

	function distinctCount(reads) {
		const ids = {};
		reads.forEach(function (r) {
			ids[r.id] = true;
		});
		return Object.keys(ids).length;
	}

	// ── Rendering ────────────────────────────────────────────────────────────

	function renderCount(count) {
		numberNodes.forEach(function (node) {
			node.textContent = String(count);
		});
		if (nounNode) {
			nounNode.textContent = count === 1 ? 'article' : 'articles';
		}
	}

	function showState(state) {
		// state: 'panel' | 'optedout' | 'hidden'
		if (panel) panel.hidden = state !== 'panel';
		if (optedOutRow) optedOutRow.hidden = state !== 'optedout';
		root.hidden = state === 'hidden';
	}

	// ── Evaluate ─────────────────────────────────────────────────────────────
	// `forceReveal` bypasses the reveal threshold — used when the reader has just
	// switched counting back on, so the panel doesn't vanish on them.

	function evaluate(forceReveal) {
		if (!hasAnalyticsConsent()) {
			// No consent to store anything — record nothing, imply nothing.
			clearReads();
			showState('hidden');
			return;
		}

		if (isOptedOut()) {
			clearReads();
			renderCount(0);
			showState('optedout');
			return;
		}

		const count = distinctCount(recordVisit());
		renderCount(count);

		if (forceReveal || count >= revealThreshold) {
			showState('panel');
		} else {
			showState('hidden');
		}
	}

	// ── Controls ─────────────────────────────────────────────────────────────

	if (offBtn) {
		offBtn.addEventListener('click', function () {
			setOptedOut(true);
			clearReads();
			evaluate();
		});
	}

	if (onBtn) {
		onBtn.addEventListener('click', function () {
			setOptedOut(false);
			evaluate(true);
		});
	}

	// React live to consent changes made in the cookie banner (global.js
	// dispatches this on grant/withdraw of any category).
	window.addEventListener('eauk:consentchange', function () {
		evaluate();
	});

	evaluate();
})();
